import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseNavigationGuardOptions {
    when: boolean;
}

export interface UseNavigationGuardReturn {
    isBlocked: boolean;
    confirm: () => void;
    cancel: () => void;
}

interface PendingNavigation {
    type: 'push' | 'pop';
    state?: any;
    unused?: string;
    url?: string | URL | null;
}

export function useNavigationGuard({
    when,
}: UseNavigationGuardOptions): UseNavigationGuardReturn {
    const [isBlocked, setIsBlocked] = useState(false);

    const pendingNavRef = useRef<PendingNavigation | null>(null);
    const isProceedingRef = useRef(false);
    const whenRef = useRef(when);
    whenRef.current = when;

    const originalPushStateRef = useRef<typeof window.history.pushState | null>(
        null
    );
    const currentUrlRef = useRef(
        typeof window !== 'undefined' ? window.location.href : ''
    );

    const cancel = useCallback(() => {
        pendingNavRef.current = null;
        setIsBlocked(false);
    }, []);

    const confirm = useCallback(() => {
        const pending = pendingNavRef.current;
        if (!pending) return;

        isProceedingRef.current = true;
        pendingNavRef.current = null;
        setIsBlocked(false);

        if (pending.type === 'push' && originalPushStateRef.current) {
            originalPushStateRef.current(
                pending.state,
                pending.unused ?? '',
                pending.url
            );
            window.dispatchEvent(new PopStateEvent('popstate', { state: pending.state }));
        } else if (pending.type === 'pop') {
            window.history.back();
        }

        isProceedingRef.current = false;
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!when) {
            if (isBlocked) {
                cancel();
            }
            return;
        }

        currentUrlRef.current = window.location.href;

        const originalPushState = window.history.pushState.bind(window.history);
        originalPushStateRef.current = originalPushState;

        window.history.pushState = function (
            state: any,
            unused: string,
            url?: string | URL | null
        ) {
            if (isProceedingRef.current || !whenRef.current) {
                return originalPushState(state, unused, url);
            }

            if (url != null) {
                const target = new URL(String(url), window.location.href);
                if (target.pathname === window.location.pathname) {
                    return originalPushState(state, unused, url);
                }
            }

            pendingNavRef.current = { type: 'push', state, unused, url };
            setIsBlocked(true);
        };

        const handlePopState = () => {
            if (isProceedingRef.current || !whenRef.current) return;

            pendingNavRef.current = { type: 'pop' };
            setIsBlocked(true);
            originalPushState(window.history.state, '', currentUrlRef.current);
        };

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!whenRef.current) return;
            e.preventDefault();
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            originalPushStateRef.current = null;
        };
    }, [when, isBlocked, cancel]);

    return { isBlocked, confirm, cancel };
}
